import {isFeatureEnabled} from '@github-ui/feature-flags'
import {preserveStaffNavigationParams} from '@github-ui/react-app-navigation/staff-navigation-params'
import type {Location, NavigateOptions as RouterNavigateOptions, RouterProviderProps, To} from '@github-ui/react-router'
import {matchRoutes, resolvePath} from '@github-ui/react-router'
import {startSoftNav} from '@github-ui/soft-nav/state'
import {sendBailoutStats} from '@github-ui/soft-nav/stats'
import {getCurrentReactAppName} from '@github-ui/stats-metadata'
import {createPath, parsePath, type Path} from 'history'

type Router = RouterProviderProps['router']

export const GLOBAL_NAV_CONTEXT_REFRESH_NAVIGATION_STATE = 'globalNavContextRefresh'

/**
 * Wraps the router's navigate function to add support for Turbo and Soft Navigation.
 *
 * When this is called, immediately `startSoftNav` if necessary
 * Apply a default `skipTurbo` state override to all router navigations
 * Preserve staff magic params (`_features`, `_tracing`) from the current URL
 * Use document navigation for explicit cross-origin HTTP(S) URLs, honoring `replace`
 *
 * This ensures that we mark react soft navigations correctly as well as ensure turbo doesn't
 * incorrectly handle react navigations.
 *
 * @param router The router to wrap.
 * @returns A new router with the wrapped navigate function.
 */
export function wrapRouterNavigateForTurboAndSoftNavs(router: Router): Router {
  function navigate(to: To | null | number, opts?: RouterNavigateOptions): Promise<void> {
    if (typeof to === 'number') {
      return router.navigate(to)
    }

    const explicitHttpUrl = getExplicitHttpUrl(to)
    if (explicitHttpUrl && explicitHttpUrl.origin !== window.location.origin && typeof to === 'string') {
      navigation.hardNavigate(to, opts?.replace ?? false)
      return Promise.resolve()
    }

    const currentLocation = router.state.location
    const nextLocation = explicitHttpUrl
      ? {pathname: explicitHttpUrl.pathname, search: explicitHttpUrl.search, hash: explicitHttpUrl.hash}
      : typeof to === 'string'
        ? parsePath(to)
        : to

    // Preserve staff magic params from current URL
    const toWithMagicParams = preserveStaffMagicParams(nextLocation)

    const externalNav = toWithMagicParams
      ? resolveExternalNavigation(router, currentLocation, nextLocation, opts)
      : undefined
    if (toWithMagicParams && externalNav) {
      const destinationUrl = typeof toWithMagicParams === 'string' ? toWithMagicParams : createPath(toWithMagicParams)
      if (externalNav === 'reload') {
        const appName = getCurrentReactAppName() || 'rails'
        sendBailoutStats({
          startUrl: window.location.href,
          endUrl: destinationUrl,
          initiatorApp: appName,
          destinationApp: appName,
        })
        navigation.hardNavigate(destinationUrl)
        return Promise.resolve()
      }

      // Destination is outside this app's router; let the owning app load it.
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.warn(
          `[react-router] Navigating to "${destinationUrl}", which isn't served by this app's router — ` +
            `handing off to a full navigation. If this route is meant to be client-side, it may be ` +
            `missing from the app's route registration.`,
        )
      }
      return navigation.turboNavigate(destinationUrl)
    }

    const hashNav = isHashNav(currentLocation, nextLocation)
    if (!hashNav) {
      startSoftNav('react')
    }

    const isPushAction = !opts?.replace
    const skipTurboState = opts?.state?.skipTurbo

    return router.navigate(toWithMagicParams, {
      ...opts,
      // Prevent scroll reset on hash-only navigations to avoid jumping to top
      // before virtualized content can scroll to the target line
      preventScrollReset: hashNav ? true : opts?.preventScrollReset,
      state: {
        ...opts?.state,
        skipTurbo: isPushAction ? (skipTurboState ?? true) : skipTurboState,
      },
    })
  }

  return new Proxy(router, {
    get(target, prop, receiver) {
      if (prop === 'navigate') return navigate

      // Pass through all other props
      return Reflect.get(target, prop, receiver)
    },
  })
}

// exposed for testing and easier mocking
export const navigation = {
  hardNavigate(url: string, replace = false) {
    if (replace) {
      window.location.replace(url)
    } else {
      window.location.href = url
    }
  },
  async turboNavigate(url: string) {
    const {softNavigate} = await import('@github-ui/soft-navigate')
    softNavigate(url)
  },
}

// 'reload' -> full document reload; 'turbo' -> Turbo navigation handoff.
type ExternalNavigation = 'reload' | 'turbo'

function resolveExternalNavigation(
  target: Router,
  currentLocation: Location,
  nextLocation: Partial<Path> | null,
  options?: RouterNavigateOptions,
): ExternalNavigation | undefined {
  try {
    // Repo routes: navigating to a different repo does a full page reload to avoid stale data.
    const isRepoRoute = !!(target.state?.loaderData?.codeViewLayoutRoute || target.state?.loaderData?.repoLayoutRoute)
    const pathname = nextLocation?.pathname
    const resolvedPathname = pathname ? resolvePath(nextLocation, currentLocation.pathname).pathname : undefined
    if (isRepoRoute && resolvedPathname && !isSameRepoPath(currentLocation.pathname, resolvedPathname)) {
      if (!isFeatureEnabled('global_nav_context_refresh')) return 'reload'
      // The global navigation bridge marks the exact destination it arranged a context refresh for.
      if (options?.state?.[GLOBAL_NAV_CONTEXT_REFRESH_NAVIGATION_STATE] !== resolvedPathname) return 'reload'
      return undefined
    }

    // Absolute paths this router can't match would dead-end on its internal 404 boundary,
    // so hand off to a Turbo navigation and let the owning app load it. Relative paths
    // ('.', '..') resolve against the current in-app location, so we skip them here.
    if (pathname?.startsWith('/') && !matchRoutes(target.routes, pathname)) {
      return 'turbo'
    }

    return undefined
  } catch {
    // If we can't resolve the destination, fall back to a full reload rather than risk
    // dead-ending on a broken client navigation.
    return 'reload'
  }
}

function isSameRepoPath(url1: string, url2: string): boolean {
  const path1 = url1.split('/', 3).join('/')
  const path2 = url2.split('/', 3).join('/')
  return path1 === path2
}

function getExplicitHttpUrl(to: To | null): URL | undefined {
  if (typeof to !== 'string') return undefined

  try {
    const url = new URL(to, window.location.href)
    const isHttp = url.protocol === 'http:' || url.protocol === 'https:'
    const isExplicit = to.startsWith('//') || to.toLowerCase().startsWith(`${url.protocol}//`)
    return isHttp && isExplicit ? url : undefined
  } catch {
    // Leave malformed destinations to React Router's validation.
    return undefined
  }
}

/**
 * Preserves staff magic params (`_features`, `_tracing`) from the current browser URL
 * to the destination URL if they aren't already present in the destination.
 */
function preserveStaffMagicParams(to: Partial<Path> | null): To | null {
  if (!to) return null

  const preservedSearch = preserveStaffNavigationParams(to.search ?? '')
  if (preservedSearch === (to.search ?? '')) return to

  return createPath({
    ...to,
    search: preservedSearch,
  })
}

function isHashNav(current: Partial<Path>, next: Partial<Path> | null): boolean {
  if (!next) return false

  if (next.pathname !== undefined && next.pathname !== current.pathname) return false
  if (next.search !== undefined && next.search !== current.search) return false

  return Boolean(next.hash !== undefined && next.hash !== current.hash)
}
